import React from "react";
import { useParams } from "react-router-dom";
import apiClient from "../../clients/apiClient.js";
import { Box, Button, ButtonGroup, Flex, HStack, IconButton, Text } from "@chakra-ui/react";
import { Table, Thead, Tbody, Tr, Th, Td } from "@chakra-ui/react";
import { Close } from "@mui/icons-material";
import "./renderedHtml.css";

const AnnotationPage = () => {
    const [rawChunks, setRawChunks] = React.useState([]);
    const [htmlChunks, setHtmlChunks] = React.useState([]);
    const [rawHtmlChunks, setRawHtmlChunks] = React.useState([]);
    const chunkRefs = React.useRef([]);
    const [annotations, setAnnotations] = React.useState([]);
    const { annotationId } = useParams();

    const addAnnotations = (newAnnotation) => {
        apiClient.post('/annotation/annotate-actions', {
            action: "add_annotation",
            add_annotation: {
                file_id: annotationId,
                chunk_id: rawChunks[newAnnotation.chunkIndex]?.chunk_id,
                annotation: newAnnotation,
            }
        }).then((response) => {
            if (response.data?.success) {
                newAnnotation.id = response.data.add_annotation.annotation_id

                console.log("[ExportSettings] New annotation is", newAnnotation);
                const updatedAnnotations = [...annotations, newAnnotation];
                setAnnotations(arrangeAnnotations(updatedAnnotations).sort((a, b) => a.sno - b.sno));
            }
        })
    }

    const removeAnnotations = (annotation) => {
        apiClient.post('/annotation/annotate-actions', {
            action: "remove_annotation",
            remove_annotation: {
                file_id: annotationId,
                annotation: annotation,
            }
        }).then((response) => {
            if (response.data?.success) {
                const updatedAnnotations = annotations.filter(ann => ann.id !== annotation.id);
                setAnnotations(arrangeAnnotations(updatedAnnotations).sort((a, b) => a.sno - b.sno));
            }
        })
    }
  
    const handleHighlight = (chunkIdx, highlightType) => {
        const selection = window.getSelection();
        if (!selection || selection.isCollapsed) return;
    
        const range = selection.getRangeAt(0);
        console.log("Range is", range);
        const container = chunkRefs.current[chunkIdx];
        if (!container.contains(range.commonAncestorContainer)) return;

        console.log("Range is", range.startContainer, range.startOffset, range.endContainer, range.endOffset);
    
        const offset = getAccurateOffset(container, range);

        // Ensure the selected text starts exactly at the offset and ends at the endOffset
        const selectedText = selection.toString();
        const containerText = container.textContent || "";
        const calculatedText = containerText.slice(offset, offset + selectedText.length);

        // if (selectedText !== calculatedText) {
        //     console.error("Selected text does not match the calculated text from offsets.");
        //     return;
        // }
        // const selectedText = selection.toString();
        const endOffset = offset + selectedText.length;
    
        // Create a span with background color
        const span = document.createElement("span");
        span.setAttribute("data-type", highlightType);
        span.setAttribute("data-id", `ann-${chunkIdx}-${offset}-${endOffset}`);
        span.style.backgroundColor = highlightType === "question" ? "#ffe066" : "#c3f7c8";
        span.style.borderRadius = "2px";
        span.style.padding = "0 2px";
        span.textContent = selectedText;
    
        // Replace selected contents
        range.deleteContents();
        range.insertNode(span);
    
        // Update chunk HTML
        const updatedHTML = container.innerHTML;
        const updatedChunks = [...htmlChunks];
        updatedChunks[chunkIdx] = updatedHTML;
        setHtmlChunks(updatedChunks);
    
        const newAnnotation = {
            sno: annotations.length + 1,
            chunkIndex: chunkIdx,
            text: selectedText,
            type: highlightType,
            startOffset: offset,
            endOffset: endOffset,
        };
    
        addAnnotations(newAnnotation);
    
        selection.removeAllRanges();
    };
    

    function getAccurateOffset(container, range) {
        const preRange = document.createRange();
        preRange.setStart(container, 0);
        preRange.setEnd(range.startContainer, range.startOffset + (range.startContainer.nodeValue?.slice(0, range.startOffset).match(/\s*$/)?.[0]?.length || 0));
        // preRange.setEnd(range.startContainer, range.startOffset);
    
        const text = preRange.toString();
        return text.length;
    }

    const arrangeAnnotations = (annotations) => {
        annotations.sort((a, b) => a.startOffset - b.startOffset);
        for (let i = 0; i < annotations.length; i++) {
            annotations[i].position = i + 1;
        }

        return annotations;
    }
      
    React.useEffect(() => {
        apiClient.post('/annotation/get-file-chunk', {
            file: {
                file_id: annotationId,
            },
            chunk_offset_start: 1,
            chunk_offset_end: 1,
        }).then((response) => {
            setRawChunks(response.data?.chunks);
            const fetchedAnnotations = response.data?.annotations;
            if (fetchedAnnotations) {
                setAnnotations(arrangeAnnotations(response.data?.annotations).sort((a, b) => a.sno - b.sno));
            }
        })
    }, [])

    React.useEffect(() => {
        const loadChunks = async () => {
          const fetchedChunks = [];
    
          for (const chunk of rawChunks) {
            try {
              const res = await fetch(chunk.chunk_url);
              const html = await res.text();
              fetchedChunks.push(html);
            } catch (err) {
              console.error("Failed to fetch chunk:", chunk.chunk_url, err);
            }
          }
    
          setRawHtmlChunks(fetchedChunks);
        };
    
        loadChunks();

    }, [rawChunks]);

    function applyAnnotationsToHtmlChunk(html, annotations) {
        const container = document.createElement("div");
        container.innerHTML = html;
      
        // Step 1: Flatten all text nodes and track their offsets
        const flatNodes = [];
        const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
        let globalOffset = 0;
      
        while (walker.nextNode()) {
          const node = walker.currentNode;
          const length = node.nodeValue.length;
          flatNodes.push({ node, start: globalOffset, end: globalOffset + length });
          globalOffset += length;
        }
      
        // Step 2: Sort annotations in reverse order (so DOM stays stable)
        const sorted = [...annotations].sort((a, b) => b.startOffset - a.startOffset);
      
        for (const ann of sorted) {
          let { startOffset, endOffset, type, chunkIndex } = ann;
          let spanId = `ann-${chunkIndex}-${startOffset}-${endOffset}`;
          const shiftLength = 4;
          startOffset += shiftLength;
          endOffset += shiftLength;
      
          const startNodeInfo = flatNodes.find(n => startOffset >= n.start && startOffset < n.end);
          const endNodeInfo = flatNodes.find(n => endOffset > n.start && endOffset <= n.end);
      
          if (!startNodeInfo || !endNodeInfo) continue;
      
          const span = document.createElement("span");
          span.setAttribute("data-id", spanId);
          span.setAttribute("data-type", type);
          span.style.backgroundColor = type === "question" ? "#ffe066" : "#c3f7c8";
          span.style.borderRadius = "2px";
          span.style.padding = "0 2px";
      
        //   // Case 1: Single node
          if (startNodeInfo.node === endNodeInfo.node) {
            const text = startNodeInfo.node.nodeValue;
            const relStart = startOffset - startNodeInfo.start;
            const relEnd = endOffset - startNodeInfo.start;
      
            const before = document.createTextNode(text.slice(0, relStart));
            const middle = document.createTextNode(text.slice(relStart, relEnd));
            const after = document.createTextNode(text.slice(relEnd));
      
            span.appendChild(middle);
      
            const parent = startNodeInfo.node.parentNode;
            parent?.replaceChild(after, startNodeInfo.node);
            parent?.insertBefore(span, after);
            parent?.insertBefore(before, span);
          } else {
            // Case 2: Multi-node selection
            // 1. Trim start node
            const sNode = startNodeInfo.node;
            const sOffset = startOffset - startNodeInfo.start;
            const sText = sNode.nodeValue;
            const sBefore = document.createTextNode(sText.slice(0, sOffset));
            const sHighlight = document.createTextNode(sText.slice(sOffset));
            const sParent = sNode.parentNode;
            sParent?.replaceChild(sHighlight, sNode);
            sParent?.insertBefore(sBefore, sHighlight);
      
            span.appendChild(sHighlight);

            let capturing = false;
            for (const n of flatNodes) {
              if (n === startNodeInfo) {
                capturing = true;
                continue;
              }
              if (n === endNodeInfo) break;
              if (capturing) {
                span.appendChild(n.node.cloneNode());
                if (n.node.parentNode) {
                  n.node.parentNode.removeChild(n.node);
                }
              }
            }
      
            // 3. Trim end node
            const eNode = endNodeInfo.node;
            const eOffset = endOffset - endNodeInfo.start;
            const eText = eNode.nodeValue;
            const eHighlight = document.createTextNode(eText.slice(0, eOffset));
            const eAfter = document.createTextNode(eText.slice(eOffset));
            const eParent = eNode.parentNode;
            eParent?.replaceChild(eAfter, eNode);
            eParent?.insertBefore(eHighlight, eAfter);
      
            span.appendChild(eHighlight);
      
            eParent?.insertBefore(span, eAfter);
          }
        }
      
        return container.innerHTML;
    }
      
    React.useEffect(() => {
        const updated = rawHtmlChunks.map((chunk, idx) =>
            applyAnnotationsToHtmlChunk(
            chunk,
            annotations.filter((a) => a.chunkIndex === idx)
            )
        );
        setHtmlChunks(updated);
    }, [rawHtmlChunks]);
    

    const sanitizeHTML = (html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
      
        const images = doc.querySelectorAll('img');
        images.forEach((img) => img.remove());

        // Enhance empty <td> for interaction
        const tds = doc.querySelectorAll('td');
        tds.forEach(td => {
            const isEmpty = !td.textContent.trim();
            if (isEmpty) {
            td.classList.add('selectable-empty');
            td.innerHTML = '&nbsp;'; // ensures it's clickable
            }
        })

        return doc.documentElement.outerHTML;
    };

    document.querySelectorAll('.selectable-empty').forEach(td => {
        td.addEventListener('click', () => {
          document.querySelectorAll('.selectable-empty').forEach(el => el.classList.remove('selected'));
          td.classList.add('selected');
        });
    });

    function scrollToAnnotation(annotation) {
        const id = `ann-${annotation.chunkIndex}-${annotation.startOffset}-${annotation.endOffset}`;
        console.log("Scrolling to ID:", id);
        const el = document.querySelector(`[data-id="${id}"]`);
        if (el) {
            console.log("Found element:", el);
            el.scrollIntoView({ behavior: "smooth", block: "center" });
            el.style.backgroundColor = "#fff3cd"; // Optional: temporary highlight
            setTimeout(() => {
                el.style.backgroundColor = annotation.type === "question" ? "#ffe066" : "#c3f7c8";
            }, 2000);
        }
    }

    return (
        <Box minH="100vh" maxH={"100vh"} w="99vw" overflow={"hidden"}>
            <Flex height="100%" maxH={"100%"} w="100%" maxW={"100%"} m={0} p={0}>
                {/* Left half: Main content */}
                <Box h={"100vh"} maxH={"100vh"} w="50%" p={4} pr={0} overflowY="scroll" data-scrollable="true">
                {htmlChunks.map((chunk, idx) => (
                    <Box key={idx} m={0} mb={6} maxH={"100%"}>
                        <Box
                            ref={(el) => (chunkRefs.current[idx] = el)}
                            w="100%"
                            border="1px solid #ddd"
                            borderRadius="md"
                            boxShadow="sm"
                            bg="white"
                            p={"32px"}
                            dangerouslySetInnerHTML={{ __html: sanitizeHTML(chunk) }}
                        />
                    </Box>
                ))}
                </Box>
                <Box
                    w={"50%"}
                    h={"100vh"}
                    maxH={"100vh"}
                >
                    <Box height={"120px"} m={"16px"} p={"16px"}>
                        <HStack
                            justifyContent={"space-between"}
                            alignItems={"center"}
                            mb={2}
                        >
                            <Text
                                fontSize={"20px"}
                                fontWeight={"bold"}
                            >
                                Annotate
                            </Text>
                            <Button
                                color={"white"}
                                backgroundColor={"#2584FD"}
                                _hover={{
                                    backgroundColor: "#2584FD",
                                }}
                            >
                                Complete Tagging
                            </Button>
                        </HStack>
                        <ButtonGroup variant="outline" size="sm" mb={2}>
                            <Button
                            colorScheme={"yellow"}
                            onClick={() => handleHighlight(0, "question")}
                            >
                                Annotate as Question
                            </Button>
                            <Button
                            colorScheme={"green"}
                            onClick={() => handleHighlight(0, "answer")}
                            >
                                Annotate as Answer
                            </Button>
                        </ButtonGroup>
                    </Box>
                    <Box height={`calc(100% - 152px - 16px)`} maxHeight={`calc(100% - 152px - 16px)`} overflowY={"scroll"} border={"1px solid #ddd"} margin={"16px"} mt={0}>
                        <Table variant="simple" size="sm">
                            <Thead>
                                <Tr>
                                    <Th width="72px" textAlign="center">S No.</Th>
                                    <Th textAlign="center">Text</Th>
                                    <Th textAlign="center">Type</Th>
                                    <Th textAlign="center">Position</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {annotations.map((annotation, index) => (
                                    <Tr
                                        key={index}
                                        onClick={() => scrollToAnnotation(annotation)}
                                        _hover={{ backgroundColor: "#f0f0f0", cursor: "pointer" }}
                                    >
                                        <Td textAlign="center">{annotation.sno}</Td>
                                        <Td textAlign="left">{annotation.text}</Td>
                                        <Td textAlign="center">{annotation.type.charAt(0).toUpperCase() + annotation.type.slice(1)}</Td>
                                        <Td textAlign="center">{annotation.position}</Td>
                                        <Td textAlign="center">
                                            <IconButton
                                                sx={{
                                                    backgroundColor: "transparent",
                                                    padding: "0",
                                                    margin: "0",
                                                }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeAnnotations(annotation);
                                                } }
                                            >
                                                <Close
                                                    sx={{ 
                                                        color: "#1d1d1d"
                                                    }}
                                                />
                                            </IconButton>
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </Box>
                </Box>
            </Flex>
        </Box>
    )   
}

export default AnnotationPage